import React, { useState } from 'react';
import styled from 'styled-components';
import { Color, Card, Text, message, Title } from '@bb/elements-web';
import BillingCard from './BillingCard';
import { ViewingLevel } from '../..';
import { useSubscriptionsPageContext } from '../../SubscriptionsPageContext';
import ManagePaymentMethodCard from '../../../../components/ManagePaymentCard/ManagePaymentMethodCard';
import { useUpdatePaymentCardModalContext } from '../../../../components/ManagePaymentCard/UpdatePaymentCardModalContext';
import UpdatePaymentCardModal from '../../../../components/ManagePaymentCard/UpdatePaymentCardModal';
import { PayNowModal } from '../../../../components/ManagePaymentCard/PayNowModal';
import { CardErrorTag } from '../CardErrorTag';

export type PaymentCardProps = {
  /** Whether these are the subscriptions for an individual user or for a brokerage paying for all its users */
  viewingLevel: ViewingLevel;
};

/**
 * Displays the Payment card on the Subscriptions & Payments page.
 * This card can be in a few different states, including:
 * - Payment in good standing: displays the user's next invoice date
 * - Payment past due: displays the user's outstanding balance and next invoice date
 * - No active subscriptions: displays a message indicating no paid subscriptions
 */
const PaymentCard: React.FC<PaymentCardProps> = ({ viewingLevel }) => {
  const {
    hasOverdueBalance,
    updateBillingProfile,
    billingProfile,
    isFetchingProfile,
    isUpdatingProfile,
    canManageBilling,
  } = useSubscriptionsPageContext();
  const { setModalVisible, updatedPaymentInfo, hasUpdatedPaymentInfo } =
    useUpdatePaymentCardModalContext();

  const [openPayNowModal, setOpenPayNowModal] = useState(false);
  const [handlingPayNow, setHandlingPayNow] = useState(false);

  /** Handles the completion of the Pay Now modal. */
  const handlePayNowComplete = async () => {
    if (!billingProfile) {
      message.error(
        'Billing profile not found. A billing profile is required to pay your balance.',
      );
      return;
    }

    if (!hasUpdatedPaymentInfo) {
      message.error(
        'Please update your payment method before paying your balance.',
      );
      return;
    }

    setHandlingPayNow(true);

    try {
      await updateBillingProfile(
        {
          ...billingProfile,
          JPUid: updatedPaymentInfo?.JPUid,
          CCNumber: updatedPaymentInfo?.ccNumber,
          MaskedCCNumber: updatedPaymentInfo?.maskedCCNumber,
          CCExpiryDate: updatedPaymentInfo?.ccExpiryDate,
          CardholderName: updatedPaymentInfo?.cardholderName,
          CCType: updatedPaymentInfo?.ccType,
        },
        true,
      );
    } catch (error) {
      message.error('Payment failed. Please try again.');
    } finally {
      setOpenPayNowModal(false);
      setHandlingPayNow(false);
    }
  };

  /** Click handler for canceling out of the pay now modal */
  const handlePayNowCancel = () => {
    setOpenPayNowModal(false);
  };

  /** Click handler for the payment method card. */
  const handlePaymentMethodClick = () => {
    if (hasOverdueBalance) {
      setOpenPayNowModal(true);
    } else {
      setModalVisible(true);
    }
  };

  return (
    <>
      <UpdatePaymentCardModal
        showFooter
        billingProfile={billingProfile}
        updateBillingProfile={
          hasOverdueBalance ? undefined : updateBillingProfile
        }
        isUpdatingProfile={hasOverdueBalance ? undefined : isUpdatingProfile}
      />

      <PayNowModal
        open={openPayNowModal}
        onComplete={handlePayNowComplete}
        onCancel={handlePayNowCancel}
        completionDisabled={!hasUpdatedPaymentInfo || handlingPayNow}
        completionLoading={handlingPayNow}
        showCardErrorWarning={hasOverdueBalance}
      />

      <Card
        title={
          <Title dataCy="payment-card-title" level={4} margin={0}>
            Payment
          </Title>
        }
        dataCy="payment-card"
      >
        <Subtitle size="large" color="SECONDARY_TEXT">
          All subscriptions are billed in the same billing cycle
        </Subtitle>
        <StyledBody>
          <StyledSection>
            <Title level={4} dataCy="billing-title" margin={0}>
              {hasOverdueBalance ? 'Outstanding balance' : 'Billing'}
            </Title>
            {/* The Billing Card (left half) */}
            <BillingCard
              canManageBilling={canManageBilling}
              isSubscriptionOverdue={hasOverdueBalance}
              viewingLevel={viewingLevel}
              onPayNowClick={() => setOpenPayNowModal(true)}
            />
          </StyledSection>

          <StyledSection>
            <StyledRow>
              <Title level={4} dataCy="payment-method-title" margin={0}>
                Payment method
              </Title>
              {hasOverdueBalance && <CardErrorTag>Card Error</CardErrorTag>}
            </StyledRow>
            {/* The Payment Method Card (right half) */}
            <ManagePaymentMethodCard
              onUpdateCardClick={handlePaymentMethodClick}
              showCardErrorWarning={false} // Dont show the card error warning here
              billingProfile={billingProfile}
              isFetchingProfile={isFetchingProfile}
              canManageBilling={canManageBilling}
            />
          </StyledSection>
        </StyledBody>
      </Card>
    </>
  );
};

export default PaymentCard;

// Styled components

const Subtitle = styled(Text)`
  display: block;
  margin: -5px 0 16px 0;
`;

const StyledBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-row-gap: 25px;
  grid-column-gap: 25px;
  border-radius: 8px;
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid ${Color.LIGHTER_GREY};
  gap: 20px;
  border-radius: 4px;
  padding: 24px;
`;
